import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PLATAFORMAS_APP, type ActualizarVersionApp, type PlataformaApp, type VersionAppInfo } from '@cmms/shared';
import { api, ErrorApi } from '../../servicios/api.js';
import { ErroresFormulario } from '../../componentes/piezas.js';
import { useSesionOperador } from '../../servicios/sesion-operador.js';
import { Aviso, CasillaVerificacion, Cargando, Dialogo, Etiqueta, type Tono, Vacio } from '../../componentes/piezas.js';

export const ETIQUETA_PLATAFORMA: Record<PlataformaApp, string> = {
  android: 'Android',
  ios: 'iOS',
  web: 'Web',
  tablet: 'Tablet (APK sideload)',
};

/** Resume de un vistazo qué le pasaría a un usuario en esa plataforma AHORA MISMO, con lo ya guardado. */
function estadoActual(info: VersionAppInfo): { tono: Tono; texto: string } {
  if (info.modoMantenimiento) return { tono: 'roja', texto: 'En mantenimiento' };
  if (info.versionActual && info.forzarActualizacion) return { tono: 'roja', texto: 'Actualización forzada' };
  if (info.versionActual) return { tono: 'azul', texto: 'Aviso opcional' };
  if (info.versionMinima) return { tono: 'ambar', texto: 'Piso mínimo activo' };
  return { tono: 'gris', texto: 'Sin restricción' };
}

/**
 * Panel de versión mínima / mantenimiento por plataforma (`platform.versiones_app`).
 *
 * Lista, no tarjetas: con cuatro filas fijas (una por plataforma) una tabla se lee
 * de un vistazo —qué plataforma tiene qué activo— sin tener que escanear cuatro
 * formularios completos para encontrar la que importa.
 */
export function ListaVersionesApp(): JSX.Element {
  const { puede } = useSesionOperador();
  return (
    <TablaVersionesApp puedeEscribir={puede('version_app:escribir')} rutaBase="/plataforma/versiones-app">
      <h1>Versiones de la app</h1>
      <p>
        Versión mínima instalable, aviso de versión nueva y mantenimiento, por plataforma. Un cambio aquí
        surte efecto la próxima vez que cada instalación abra la app o vuelva a primer plano.
      </p>
    </TablaVersionesApp>
  );
}

/**
 * Compartida entre la consola de plataforma y "Gestión" (el mismo panel, dos
 * rutas de API distintas por los guards distintos que las protegen — ver
 * `GestionController` vs `PlataformaController`). `children` es la cabecera
 * (h1 + texto): cada página trae la suya porque el texto de advertencia no es
 * el mismo desde el admin de un tenant que desde la consola de plataforma.
 */
export function TablaVersionesApp({
  puedeEscribir,
  rutaBase,
  children,
}: {
  puedeEscribir: boolean;
  rutaBase: string;
  children: ReactNode;
}): JSX.Element {
  const cliente = useQueryClient();
  const [editando, setEditando] = useState<PlataformaApp | null>(null);

  const { data: versiones, isLoading } = useQuery({
    queryKey: [rutaBase],
    queryFn: () => api.get<VersionAppInfo[]>(rutaBase),
  });

  const guardar = useMutation({
    mutationFn: ({ plataforma, datos }: { plataforma: PlataformaApp; datos: ActualizarVersionApp }) =>
      api.put<VersionAppInfo>(`${rutaBase}/${plataforma}`, datos),
    onSuccess: async () => {
      setEditando(null);
      await cliente.invalidateQueries({ queryKey: [rutaBase] });
    },
  });

  if (isLoading) return <Cargando />;

  // Las cuatro filas las crea la migración: siempre están las cuatro, en el
  // mismo orden fijo de `PLATAFORMAS_APP`, no en el orden en que responda la BD.
  const porPlataforma = new Map((versiones ?? []).map((v) => [v.plataforma, v]));
  const filas = PLATAFORMAS_APP.map((p) => porPlataforma.get(p)).filter((v): v is VersionAppInfo => v != null);
  const infoEditando = editando ? porPlataforma.get(editando) : undefined;

  return (
    <>
      <div className="cabecera-pagina">
        <div>{children}</div>
      </div>

      {guardar.isError ? <ErroresFormulario error={guardar.error} /> : null}

      {filas.length === 0 ? (
        <Vacio titulo="Sin datos" detalle="Corre las migraciones para sembrar las cuatro plataformas." />
      ) : (
        <section className="panel">
          <div className="tabla-contenedor">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Plataforma</th>
                  <th>Versión mínima</th>
                  <th>Última versión publicada</th>
                  <th>Estado</th>
                  <th>Actualizado</th>
                  {puedeEscribir ? <th /> : null}
                </tr>
              </thead>
              <tbody>
                {filas.map((info) => {
                  const estado = estadoActual(info);
                  return (
                    <tr key={info.plataforma}>
                      <td style={{ fontWeight: 600 }}>{ETIQUETA_PLATAFORMA[info.plataforma]}</td>
                      <td className="numero-registro">{info.versionMinima ?? '—'}</td>
                      <td className="numero-registro">{info.versionActual ?? '—'}</td>
                      <td>
                        <Etiqueta tono={estado.tono}>{estado.texto}</Etiqueta>
                      </td>
                      <td style={{ fontSize: 12.5, color: 'var(--texto-tenue)' }}>
                        {new Date(info.actualizadoEl).toLocaleString('es-PE')}
                      </td>
                      {puedeEscribir ? (
                        <td style={{ textAlign: 'right' }}>
                          <button
                            type="button"
                            className="boton secundario pequeno"
                            onClick={() => setEditando(info.plataforma)}
                          >
                            Editar
                          </button>
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {infoEditando ? (
        <Dialogo
          titulo={`Editar ${ETIQUETA_PLATAFORMA[infoEditando.plataforma]}`}
          ancho={560}
          alCerrar={() => setEditando(null)}
        >
          <FormularioVersionApp
            info={infoEditando}
            guardando={guardar.isPending}
            onGuardar={(datos) => guardar.mutate({ plataforma: infoEditando.plataforma, datos })}
          />
        </Dialogo>
      ) : null}
    </>
  );
}

/**
 * Describe en texto llano qué pasaría con lo que hay tipeado AHORA en el
 * formulario —no lo ya guardado—, para que quien edita vea el efecto antes de
 * guardar, no lo tenga que deducir de los campos.
 */
function previsualizarEfecto(borrador: {
  versionMinima: string;
  versionActual: string;
  forzarActualizacion: boolean;
  modoMantenimiento: boolean;
}): string[] {
  if (borrador.modoMantenimiento) {
    return ['Bloqueará TODA la app en esta plataforma, sin importar la versión instalada, hasta que apagues el mantenimiento.'];
  }

  const efectos: string[] = [];
  if (borrador.versionMinima.trim()) {
    efectos.push(
      `Cualquier instalación con versión menor a ${borrador.versionMinima.trim()} quedará bloqueada de forma obligatoria (piso mínimo, no depende del interruptor de abajo).`,
    );
  }
  if (borrador.versionActual.trim()) {
    efectos.push(
      borrador.forzarActualizacion
        ? `Cualquier instalación con versión menor a ${borrador.versionActual.trim()} (y por encima del piso mínimo) también quedará bloqueada de forma obligatoria.`
        : `Cualquier instalación con versión menor a ${borrador.versionActual.trim()} (y por encima del piso mínimo) verá un aviso que puede descartar («Más tarde»), sin bloquearse.`,
    );
  }
  if (efectos.length === 0) {
    efectos.push('No bloqueará ni avisará a nadie: sin versión mínima ni versión publicada, todas las instalaciones pasan.');
  }
  return efectos;
}

export function FormularioVersionApp({
  info,
  guardando,
  onGuardar,
}: {
  info: VersionAppInfo;
  guardando: boolean;
  onGuardar: (datos: ActualizarVersionApp) => void;
}): JSX.Element {
  const [versionMinima, setVersionMinima] = useState(info.versionMinima ?? '');
  const [versionActual, setVersionActual] = useState(info.versionActual ?? '');
  const [urlTienda, setUrlTienda] = useState(info.urlTienda ?? '');
  const [urlDescarga, setUrlDescarga] = useState(info.urlDescarga ?? '');
  const [forzarActualizacion, setForzarActualizacion] = useState(info.forzarActualizacion);
  const [modoMantenimiento, setModoMantenimiento] = useState(info.modoMantenimiento);
  const [mensajeMantenimiento, setMensajeMantenimiento] = useState(info.mensajeMantenimiento ?? '');

  const idPrefijo = `version-app-${info.plataforma}`;
  const efectos = previsualizarEfecto({ versionMinima, versionActual, forzarActualizacion, modoMantenimiento });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onGuardar({
          versionMinima: versionMinima.trim() || undefined,
          versionActual: versionActual.trim() || undefined,
          urlTienda: urlTienda.trim() || undefined,
          urlDescarga: urlDescarga.trim() || undefined,
          forzarActualizacion,
          modoMantenimiento,
          mensajeMantenimiento: mensajeMantenimiento.trim() || undefined,
        });
      }}
    >
      {/* Lo que hay guardado AHORA, fijo, para no tener que salir del diálogo a compararlo
          con lo que se está por escribir en los campos de abajo. */}
      <Aviso tono="info">
        Actualmente registrado: versión mínima <strong>{info.versionMinima ?? 'sin fijar'}</strong> · última versión
        publicada <strong>{info.versionActual ?? 'sin anunciar'}</strong>
        {info.modoMantenimiento ? (
          <>
            {' · '}
            <strong>en mantenimiento</strong>
          </>
        ) : null}
        .
      </Aviso>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <div className="campo">
          <label htmlFor={`${idPrefijo}-minima`}>Versión mínima</label>
          <input
            id={`${idPrefijo}-minima`}
            value={versionMinima}
            onChange={(e) => setVersionMinima(e.target.value)}
            placeholder="Sin mínimo (no bloquea)"
          />
        </div>
        <div className="campo">
          <label htmlFor={`${idPrefijo}-actual`}>Última versión publicada</label>
          <input
            id={`${idPrefijo}-actual`}
            value={versionActual}
            onChange={(e) => setVersionActual(e.target.value)}
            placeholder="Sin anunciar"
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <div className="campo">
          <label htmlFor={`${idPrefijo}-tienda`}>Link a la tienda</label>
          <input
            id={`${idPrefijo}-tienda`}
            type="url"
            value={urlTienda}
            onChange={(e) => setUrlTienda(e.target.value)}
            placeholder="https://play.google.com/…"
          />
        </div>
        <div className="campo">
          <label htmlFor={`${idPrefijo}-descarga`}>Link de descarga directa (APK sideload)</label>
          <input
            id={`${idPrefijo}-descarga`}
            type="url"
            value={urlDescarga}
            onChange={(e) => setUrlDescarga(e.target.value)}
            placeholder="https://…/app-release.apk"
          />
        </div>
      </div>

      <div className="campo">
        <div className="fila-checklist">
          <CasillaVerificacion
            marcada={forzarActualizacion}
            alCambiar={() => setForzarActualizacion((v) => !v)}
            etiqueta="Forzar la actualización hasta la última versión"
          />
          <span>Forzar la actualización hasta la última versión</span>
        </div>
      </div>

      <div className="campo">
        <div className="fila-checklist">
          <CasillaVerificacion
            marcada={modoMantenimiento}
            alCambiar={() => setModoMantenimiento((v) => !v)}
            etiqueta="Modo mantenimiento (bloquea toda la app, sin importar la versión)"
          />
          <span>Modo mantenimiento (bloquea toda la app, sin importar la versión)</span>
        </div>
      </div>

      {modoMantenimiento ? (
        <div className="campo">
          <label htmlFor={`${idPrefijo}-mensaje`}>Mensaje de mantenimiento</label>
          <input
            id={`${idPrefijo}-mensaje`}
            value={mensajeMantenimiento}
            onChange={(e) => setMensajeMantenimiento(e.target.value)}
            placeholder="Estamos realizando mejoras. Intenta más tarde."
            maxLength={500}
          />
        </div>
      ) : null}

      {/* Efecto de lo tipeado, no de lo guardado: se recalcula en cada tecla. */}
      <Aviso tono={modoMantenimiento ? 'atencion' : 'info'}>
        <strong>Si guardas esto:</strong>
        <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
          {efectos.map((texto, i) => (
            <li key={i}>{texto}</li>
          ))}
        </ul>
      </Aviso>

      <button type="submit" className="boton" disabled={guardando} style={{ marginTop: 10 }}>
        {guardando ? 'Guardando…' : 'Guardar'}
      </button>
    </form>
  );
}
